import React, { useState } from 'react';
import { FileText, Search, Filter, Plus, Calendar, CheckCircle, Clock, AlertCircle, ChevronRight, Edit3, Download, Copy, X, Save, Eye } from 'lucide-react';
export function ContractManagementPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isContractDetailModalOpen, setIsContractDetailModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [isTemplatePreviewOpen, setIsTemplatePreviewOpen] = useState(false);
  // Mock data for contracts
  const contracts = [{
    id: 1,
    title: 'Rights Transfer - The Secret of the Ancient Forest',
    template: 'Standard Rights Transfer',
    campaign: 'The Secret of the Ancient Forest - Special Edition',
    status: 'active',
    date: '15/04/2023',
    parties: ['Marco Rossi', 'Aurora Editions LLC'],
    percentageCeded: 30,
    duration: '5 years'
  }, {
    id: 2,
    title: 'Distribution Contract - Lost Souls',
    template: 'Digital Distribution',
    campaign: 'Lost Souls - Kindle Edition',
    status: 'pending',
    date: '03/05/2023',
    parties: ['Marco Rossi', 'Amazon KDP'],
    percentageCeded: 25,
    duration: '3 years'
  }, {
    id: 3,
    title: 'Partial Transfer - The Road Home',
    template: 'Premium Rights Transfer',
    campaign: 'The Road Home - Audiobook',
    status: 'expired',
    date: '10/01/2022',
    parties: ['Marco Rossi', 'Audible Inc.'],
    percentageCeded: 40,
    duration: '1 year'
  }];
  // Mock data for contract templates
  const contractTemplates = [{
    id: 1,
    title: 'Standard Rights Transfer',
    description: 'Basic template for copyright transfer with variable percentage',
    usageCount: 245,
    previewText: `COPYRIGHT TRANSFER AGREEMENT
Between:
- [AUTHOR NAME], residing at [ADDRESS], [CITY] ("Author")
- [TRANSFEREE NAME], with headquarters at [ADDRESS], [CITY] ("Transferee")
WHEREAS:
- The Author is the exclusive holder of the copyright to the work titled "[WORK TITLE]"
- The Transferee wishes to acquire the economic exploitation rights of the Work
IT IS AGREED AS FOLLOWS:
1. SUBJECT OF THE AGREEMENT
The Author transfers to the Transferee, who accepts, [PERCENTAGE]% of the economic exploitation rights of the Work for a duration of [DURATION] years.
2. TRANSFERRED RIGHTS
The transferred rights include, but are not limited to:
- Right of reproduction in any form and manner
- Right of publication and distribution
- Right of translation into other languages
- Right of adaptation for other means of communication`
  }, {
    id: 2,
    title: 'Digital Distribution',
    description: 'Specific for digital distribution platforms like Amazon KDP, Kobo, etc.',
    usageCount: 189,
    previewText: `DIGITAL DISTRIBUTION AGREEMENT
Between:
- [AUTHOR NAME], residing at [ADDRESS], [CITY] ("Author")
- [PLATFORM NAME], with headquarters at [ADDRESS], [CITY] ("Distributor")
WHEREAS:
- The Author is the exclusive holder of the copyright to the work titled "[WORK TITLE]"
- The Distributor manages a digital distribution platform for editorial content
IT IS AGREED AS FOLLOWS:
1. SUBJECT OF THE AGREEMENT
The Author grants the Distributor the non-exclusive right to distribute the Work in digital format through its platform for a duration of [DURATION] years.
2. ROYALTIES
The Distributor will pay the Author [PERCENTAGE]% of the net sale price for each copy of the Work sold.
3. REPORTING
The Distributor will provide the Author with a quarterly sales report and will process royalty payments within 60 days from the end of each quarter.`
  }, {
    id: 3,
    title: 'Premium Rights Transfer',
    description: 'Advanced version with additional clauses for high-value projects',
    usageCount: 124,
    previewText: `COPYRIGHT TRANSFER AGREEMENT - PREMIUM VERSION
Between:
- [AUTHOR NAME], residing at [ADDRESS], [CITY] ("Author")
- [TRANSFEREE NAME], with headquarters at [ADDRESS], [CITY] ("Transferee")
WHEREAS:
- The Author is the exclusive holder of the copyright to the work titled "[WORK TITLE]"
- The Transferee wishes to acquire the economic exploitation rights of the Work
- The parties recognize the high commercial and cultural value of the Work
IT IS AGREED AS FOLLOWS:
1. SUBJECT OF THE AGREEMENT
The Author transfers to the Transferee, who accepts, [PERCENTAGE]% of the economic exploitation rights of the Work for a duration of [DURATION] years.
2. TRANSFERRED RIGHTS
The transferred rights include, but are not limited to:
- Right of reproduction in any form and manner
- Right of publication and distribution
- Right of translation into other languages
- Right of adaptation for other means of communication
- Merchandising rights
- Audiovisual adaptation rights
3. COMPENSATION
The Transferee will pay the Author:
- An advance of € [AMOUNT] upon signing this agreement
- Royalties equal to [PERCENTAGE]% on the cover price for each copy sold
- [PERCENTAGE]% on revenues derived from the exploitation of secondary rights`
  }, {
    id: 4,
    title: 'Audiobook Contract',
    description: 'Specific for the transfer of audiobook production rights',
    usageCount: 87,
    previewText: `AUDIOBOOK RIGHTS TRANSFER AGREEMENT
Between:
- [AUTHOR NAME], residing at [ADDRESS], [CITY] ("Author")
- [PRODUCER NAME], with headquarters at [ADDRESS], [CITY] ("Producer")
WHEREAS:
- The Author is the exclusive holder of the copyright to the work titled "[WORK TITLE]"
- The Producer intends to create an audio version of the Work
IT IS AGREED AS FOLLOWS:
1. SUBJECT OF THE AGREEMENT
The Author grants the Producer the exclusive right to produce, publish and market an audio version of the Work for a duration of [DURATION] years.
2. PRODUCTION
The Producer commits to:
- Select a professional narrator for the reading of the Work
- Produce according to high quality standards
- Complete the production within [NUMBER] months from the signing of this agreement
3. COMPENSATION
The Producer will pay the Author:
- An advance of € [AMOUNT] upon signing this agreement
- Royalties equal to [PERCENTAGE]% on net revenues derived from the sale of the audiobook`
  }, {
    id: 5,
    title: 'Translation Contract',
    description: 'For the transfer of translation rights into other languages',
    usageCount: 156,
    previewText: `TRANSLATION RIGHTS TRANSFER AGREEMENT
Between:
- [AUTHOR/PUBLISHER NAME], residing/with headquarters at [ADDRESS], [CITY] ("Rights Holder")
- [FOREIGN PUBLISHER NAME], with headquarters at [ADDRESS], [CITY] ("Publisher")
WHEREAS:
- The Rights Holder holds the copyright to the work titled "[WORK TITLE]"
- The Publisher wishes to acquire the translation and publication rights of the Work in [LANGUAGE]
IT IS AGREED AS FOLLOWS:
1. SUBJECT OF THE AGREEMENT
The Rights Holder grants the Publisher the exclusive right to translate, publish and market the Work in [LANGUAGE] in the territory of [TERRITORY] for a duration of [DURATION] years.
2. TRANSLATION
The Publisher commits to:
- Assign the translation to a qualified translator
- Submit the translation for approval by the Rights Holder before publication
- Publish the Work within [NUMBER] months from the signing of this agreement
3. COMPENSATION
The Publisher will pay the Rights Holder:
- An advance of € [AMOUNT] upon signing this agreement
- Royalties equal to [PERCENTAGE]% on the cover price for each copy sold`
  }];
  const filteredContracts = contracts.filter(contract => {
    // Filter by search query
    const matchesSearch = contract.title.toLowerCase().includes(searchQuery.toLowerCase()) || contract.campaign.toLowerCase().includes(searchQuery.toLowerCase());
    // Filter by status
    const matchesFilter = activeFilter === 'all' || contract.status === activeFilter;
    return matchesSearch && matchesFilter;
  });
  const openContractDetail = contract => {
    setSelectedContract(contract);
    setIsContractDetailModalOpen(true);
    setIsEditMode(false);
  };
  const openTemplatePreview = template => {
    setPreviewTemplate(template);
    setIsTemplatePreviewOpen(true);
  };
  const getStatusBadge = status => {
    switch (status) {
      case 'active':
        return <span className={`inline-flex items-center text-green-700 bg-[#C6D6AB] px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap`} data-id="element-426">
            <CheckCircle size={12} className="mr-1" data-id="element-427" />
            Active
          </span>;
      case 'pending':
        return <span className="inline-flex items-center text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap" data-id="element-428">
            <Clock size={12} className="mr-1" data-id="element-429" />
            Pending
          </span>;
      case 'expired':
        return <span className="inline-flex items-center text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap" data-id="element-430">
            <AlertCircle size={12} className="mr-1" data-id="element-431" />
            Expired
          </span>;
      default:
        return null;
    }
  };
  return <div className="w-full bg-gray-50 min-h-screen" data-id="element-432">
      {/* Header */}
      <div className="mb-6" data-id="element-433">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-434">
          <div data-id="element-435">
            <h1 className="text-2xl md:text-3xl font-bold text-[#164149]" data-id="element-436">
              Contracts
            </h1>
          </div>
          <button onClick={() => setIsTemplateModalOpen(true)} className="flex items-center px-4 py-2 bg-[#C6D6AB] text-gray-800 rounded-md hover:bg-[#b8c99a] transition-colors" data-id="element-437">
            <Plus size={16} className="mr-2" data-id="element-438" />
            New Contract
          </button>
        </div>
      </div>
      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200/70 shadow-sm p-4 mb-6" data-id="element-439">
        <div className="flex flex-col md:flex-row gap-4" data-id="element-440">
          <div className="relative flex-grow" data-id="element-441">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-442">
              <Search size={18} className="text-gray-400" data-id="element-443" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search contracts..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-444" />
          </div>
          <div className="flex items-center space-x-2" data-id="element-445">
            <Filter size={18} className="text-gray-500" data-id="element-446" />
            <div className="flex space-x-1" data-id="element-447">
              <button onClick={() => setActiveFilter('all')} className={`px-3 py-1.5 text-sm font-medium rounded-md ${activeFilter === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-448">
                All
              </button>
              <button onClick={() => setActiveFilter('active')} className={`px-3 py-1.5 text-sm font-medium rounded-md ${activeFilter === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-449">
                Active
              </button>
              <button onClick={() => setActiveFilter('pending')} className={`px-3 py-1.5 text-sm font-medium rounded-md ${activeFilter === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-450">
                Pending
              </button>
              <button onClick={() => setActiveFilter('expired')} className={`px-3 py-1.5 text-sm font-medium rounded-md ${activeFilter === 'expired' ? 'bg-gray-200 text-gray-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-451">
                Expired
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Contracts List */}
      {filteredContracts.length > 0 ? <div className="bg-white rounded-lg border border-gray-200/70 shadow-sm overflow-hidden" data-id="element-452">
          <div className="overflow-x-auto" data-id="element-453">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-454">
              <thead className="bg-gray-50" data-id="element-455">
                <tr data-id="element-456">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-457">
                    Contract
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-458">
                    Campaign
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-459">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-460">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-461">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-462">
                {filteredContracts.map(contract => <tr key={contract.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openContractDetail(contract)} data-id="element-463">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-464">
                      <div className="flex items-center" data-id="element-465">
                        <FileText size={18} className="text-gray-500 mr-3" data-id="element-466" />
                        <div data-id="element-467">
                          <div className="text-sm font-medium text-gray-900" data-id="element-468">
                            {contract.title}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-469">
                            {contract.template}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-470">
                      <div className="text-sm text-gray-900" data-id="element-471">
                        {contract.campaign}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-472">
                      <div className="flex items-center text-sm text-gray-500" data-id="element-473">
                        <Calendar size={14} className="mr-1" data-id="element-474" />
                        {contract.date}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-475">
                      {getStatusBadge(contract.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-476">
                      <button className="text-gray-600 hover:text-gray-900" onClick={e => {
                  e.stopPropagation();
                  openContractDetail(contract);
                }} data-id="element-477">
                        <ChevronRight size={18} data-id="element-478" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div> : <div className="bg-white rounded-lg border border-gray-200/70 shadow-sm p-8 text-center" data-id="element-479">
          <div className="max-w-2xl mx-auto" data-id="element-480">
            <FileText size={48} className="mx-auto text-gray-400 mb-4" data-id="element-481" />
            <h2 className="text-xl font-semibold text-[#164149] mb-4" data-id="element-482">
              No contracts found
            </h2>
            <p className="text-gray-600 mb-6" data-id="element-483">
              {searchQuery || activeFilter !== 'all' ? 'No contracts match your search criteria. Try modifying your filters or search.' : "You haven't created any contracts yet. Click on 'New Contract' to get started."}
            </p>
            <p className="text-sm text-gray-600 mb-6" data-id="element-484">
              Choose one of our pre-configured templates to get started. You can
              customize all details in the next step.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-485">
              {contractTemplates.map(template => <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-md transition-all" data-id="element-486">
                  <h3 className="text-lg font-semibold text-[#164149] mb-2" data-id="element-487">
                    {template.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3" data-id="element-488">
                    {template.description}
                  </p>
                  <div className="flex items-center justify-between" data-id="element-489">
                    <span className="text-xs text-gray-500" data-id="element-490">
                      Used {template.usageCount} times
                    </span>
                    <div className="flex space-x-2" data-id="element-491">
                      <button onClick={e => {
                  e.stopPropagation();
                  openTemplatePreview(template);
                }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center" data-id="element-492">
                        <Eye size={14} className="mr-1" data-id="element-493" />
                        Preview
                      </button>
                      <button onClick={() => {
                  setIsTemplateModalOpen(false);
                  // In a real app, this would open a form to customize the template
                }} className="text-xs text-gray-600 hover:text-gray-800" data-id="element-494">
                        Select
                      </button>
                    </div>
                  </div>
                </div>)}
            </div>
            <p className="text-sm text-gray-600 mb-6" data-id="element-495">
              This is a sample template. By selecting this template, you can
              customize all fields in square brackets [FIELD] with your specific
              data.
            </p>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200" data-id="element-496">
              <button onClick={() => setIsTemplateModalOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors" data-id="element-497">
                Cancel
              </button>
            </div>
          </div>
        </div>}
      {/* Template Selection Modal */}
      {isTemplateModalOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-498">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" data-id="element-499">
            <div className="p-6" data-id="element-500">
              <div className="flex items-center justify-between mb-6" data-id="element-501">
                <h2 className="text-2xl font-bold text-[#164149]" data-id="element-502">
                  Select a Template
                </h2>
                <button onClick={() => setIsTemplateModalOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-503">
                  <X size={24} data-id="element-504" />
                </button>
              </div>
              <p className="text-gray-600 mb-6" data-id="element-505">
                Choose one of our pre-configured templates to get started. You
                can customize all details in the next step.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-506">
                {contractTemplates.map(template => <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-md transition-all" data-id="element-507">
                    <h3 className="text-lg font-semibold text-[#164149] mb-2" data-id="element-508">
                      {template.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-509">
                      {template.description}
                    </p>
                    <div className="flex items-center justify-between" data-id="element-510">
                      <span className="text-xs text-gray-500" data-id="element-511">
                        Used {template.usageCount} times
                      </span>
                      <div className="flex space-x-2" data-id="element-512">
                        <button onClick={e => {
                    e.stopPropagation();
                    openTemplatePreview(template);
                  }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center" data-id="element-513">
                          <Eye size={14} className="mr-1" data-id="element-514" />
                          Preview
                        </button>
                        <button onClick={() => {
                    setIsTemplateModalOpen(false);
                    // In a real app, this would open a form to customize the template
                  }} className="text-xs text-gray-600 hover:text-gray-800" data-id="element-515">
                          Select
                        </button>
                      </div>
                    </div>
                  </div>)}
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200" data-id="element-516">
                <button onClick={() => setIsTemplateModalOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors" data-id="element-517">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Template Preview Modal */}
      {isTemplatePreviewOpen && previewTemplate && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-518">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" data-id="element-519">
            <div className="p-6" data-id="element-520">
              <div className="flex items-center justify-between mb-6" data-id="element-521">
                <h2 className="text-2xl font-bold text-[#164149]" data-id="element-522">
                  Preview: {previewTemplate.title}
                </h2>
                <button onClick={() => setIsTemplatePreviewOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-523">
                  <X size={24} data-id="element-524" />
                </button>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6 font-mono text-sm whitespace-pre-wrap" data-id="element-525">
                {previewTemplate.previewText}
              </div>
              <p className="text-sm text-gray-600 mb-6" data-id="element-526">
                This is a sample template. By selecting this template, you can
                customize all fields in square brackets [FIELD] with your
                specific data.
              </p>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200" data-id="element-527">
                <button onClick={() => setIsTemplatePreviewOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors" data-id="element-528">
                  Close
                </button>
                <button onClick={() => {
              setIsTemplatePreviewOpen(false);
              setIsTemplateModalOpen(false);
              // In a real app, this would open a form to customize the template
            }} className="px-4 py-2 bg-[#C6D6AB] text-gray-800 rounded-md hover:bg-[#b8c99a] transition-colors" data-id="element-529">
                  Select This Template
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Contract Detail Modal */}
      {isContractDetailModalOpen && selectedContract && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-530">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" data-id="element-531">
            <div className="p-6" data-id="element-532">
              <div className="flex items-center justify-between mb-6" data-id="element-533">
                <h2 className="text-2xl font-bold text-[#164149]" data-id="element-534">
                  {isEditMode ? 'Edit Contract' : 'Contract Details'}
                </h2>
                <div className="flex items-center space-x-2" data-id="element-535">
                  {!isEditMode && <>
                      <button onClick={() => setIsEditMode(true)} className="text-gray-600 hover:text-gray-900 p-1" title="Edit" data-id="element-536">
                        <Edit3 size={20} data-id="element-537" />
                      </button>
                      <button className="text-gray-600 hover:text-gray-900 p-1" title="Download PDF" data-id="element-538">
                        <Download size={20} data-id="element-539" />
                      </button>
                      <button className="text-gray-600 hover:text-gray-900 p-1" title="Duplicate" data-id="element-540">
                        <Copy size={20} data-id="element-541" />
                      </button>
                    </>}
                  <button onClick={() => {
                setIsContractDetailModalOpen(false);
                setIsEditMode(false);
              }} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-542">
                    <X size={20} data-id="element-543" />
                  </button>
                </div>
              </div>
              {/* Contract Preview/Edit Form */}
              <div className="mb-6" data-id="element-544">
                {isEditMode ? <div className="space-y-4" data-id="element-545">
                    <div data-id="element-546">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-547">
                        Contract Title
                      </label>
                      <input type="text" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={selectedContract.title} data-id="element-548" />
                    </div>
                    <div data-id="element-549">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-550">
                        Associated Campaign
                      </label>
                      <input type="text" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={selectedContract.campaign} data-id="element-551" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-552">
                      <div data-id="element-553">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-554">
                          Transferred Percentage
                        </label>
                        <input type="number" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={selectedContract.percentageCeded} data-id="element-555" />
                      </div>
                      <div data-id="element-556">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-557">
                          Duration
                        </label>
                        <input type="text" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={selectedContract.duration} data-id="element-558" />
                      </div>
                    </div>
                    <div data-id="element-559">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-560">
                        Involved Parties
                      </label>
                      <div className="space-y-2" data-id="element-561">
                        {selectedContract.parties.map((party, index) => <input key={index} type="text" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={party} data-id="element-562" />)}
                      </div>
                    </div>
                  </div> : <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-563">
                    <div className="mb-6" data-id="element-564">
                      <h3 className="text-lg font-semibold text-[#164149] mb-4 pb-2 border-b border-gray-200" data-id="element-565">
                        General Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-566">
                        <div data-id="element-567">
                          <p className="text-sm text-gray-500" data-id="element-568">Template</p>
                          <p className="text-base font-medium" data-id="element-569">
                            {selectedContract.template}
                          </p>
                        </div>
                        <div data-id="element-570">
                          <p className="text-sm text-gray-500" data-id="element-571">Date</p>
                          <p className="text-base font-medium" data-id="element-572">
                            {selectedContract.date}
                          </p>
                        </div>
                        <div data-id="element-573">
                          <p className="text-sm text-gray-500" data-id="element-574">Status</p>
                          <div className="mt-1" data-id="element-575">
                            {getStatusBadge(selectedContract.status)}
                          </div>
                        </div>
                        <div data-id="element-576">
                          <p className="text-sm text-gray-500" data-id="element-577">Campaign</p>
                          <p className="text-base font-medium" data-id="element-578">
                            {selectedContract.campaign}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mb-6" data-id="element-579">
                      <h3 className="text-lg font-semibold text-[#164149] mb-4 pb-2 border-b border-gray-200" data-id="element-580">
                        Transfer Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-581">
                        <div data-id="element-582">
                          <p className="text-sm text-gray-500" data-id="element-583">
                            Transferred Percentage
                          </p>
                          <p className="text-base font-medium" data-id="element-584">
                            {selectedContract.percentageCeded}%
                          </p>
                        </div>
                        <div data-id="element-585">
                          <p className="text-sm text-gray-500" data-id="element-586">Duration</p>
                          <p className="text-base font-medium" data-id="element-587">
                            {selectedContract.duration}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-588">
                      <h3 className="text-lg font-semibold text-[#164149] mb-4 pb-2 border-b border-gray-200" data-id="element-589">
                        Involved Parties
                      </h3>
                      <ul className="space-y-2" data-id="element-590">
                        {selectedContract.parties.map((party, index) => <li key={index} className="text-base" data-id="element-591">
                            {party}
                          </li>)}
                      </ul>
                    </div>
                  </div>}
              </div>
              {/* Contract Document Preview */}
              <div className="mb-6" data-id="element-592">
                <div className="flex items-center justify-between mb-3" data-id="element-593">
                  <h3 className="text-lg font-semibold text-[#164149]" data-id="element-594">
                    Document Preview
                  </h3>
                  <button className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-595">
                    <Eye size={16} className="mr-1" data-id="element-596" />
                    View full document
                  </button>
                </div>
                <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 h-40 overflow-hidden text-gray-500 text-sm" data-id="element-597">
                  <p data-id="element-598">COPYRIGHT TRANSFER AGREEMENT</p>
                  <p className="mt-2" data-id="element-599">Between:</p>
                  <p data-id="element-600">
                    - Marco Rossi, residing at Via Roma 123, Milan ("Author")
                  </p>
                  <p data-id="element-601">
                    - Aurora Editions LLC, with headquarters at Via Vittorio
                    Emanuele 45, Rome ("Transferee")
                  </p>
                  <p className="mt-2" data-id="element-602">WHEREAS:</p>
                  <p data-id="element-603">
                    - The Author is the exclusive holder of the copyright to the
                    work titled "The Secret of the Ancient Forest"
                  </p>
                  <p data-id="element-604">
                    - The Transferee wishes to acquire the economic exploitation
                    rights of the Work
                  </p>
                  <p className="mt-2" data-id="element-605">IT IS AGREED AS FOLLOWS:</p>
                  <p data-id="element-606">1. SUBJECT OF THE AGREEMENT</p>
                  <p data-id="element-607">
                    The Author transfers to the Transferee, who accepts, 30% of
                    the economic exploitation rights of the Work...
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200" data-id="element-608">
                {isEditMode ? <>
                    <button onClick={() => setIsEditMode(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors" data-id="element-609">
                      Cancel
                    </button>
                    <button onClick={() => setIsEditMode(false)} className="px-4 py-2 bg-[#C6D6AB] text-gray-800 rounded-md hover:bg-[#b8c99a] transition-colors flex items-center" data-id="element-610">
                      <Save size={16} className="mr-2" data-id="element-611" />
                      Save Changes
                    </button>
                  </> : <button onClick={() => setIsContractDetailModalOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors" data-id="element-612">
                    Close
                  </button>}
              </div>
            </div>
          </div>
        </div>}
    </div>;
}